/**
 * Transformation utility to convert backend metadata response to usable filter config
 *
 * Simplified version - no longer depends on dashboard registry.
 * Components are rendered directly in React, this just handles filter transformations.
 */

export interface OperatorProficiencyMetadata {
  id: string;
  version: string;
  filters: FilterConfig[];
}

export interface FilterConfig {
  id: string;
  type: 'dropdown' | 'multi-select' | 'checkbox' | 'search';
  label: string;
  options: FilterOption[];
  defaultValue?: string | string[];
}

export interface FilterOption {
  id: string;
  label: string;
  value: string | number;
  checked?: boolean;
}

/**
 * Transform backend metadata response to usable filter config
 */
export function transformOperatorProficiencyMetadata(backendMetadata: any): OperatorProficiencyMetadata {
  // Filters are in tabs[0].filters with a nested group structure
  const rawFilters = backendMetadata.tabs?.[0]?.filters || backendMetadata.filters || [];

  const filters: FilterConfig[] = rawFilters.map((filterGroup: any) => {
    // Handle nested group structure: filter.options[0].options contains actual options
    const nestedFilter = filterGroup.options?.[0];
    const actualOptions = nestedFilter?.options || filterGroup.options || [];

    return {
      id: filterGroup.id,
      type: nestedFilter?.isMultiSelected ? 'multi-select' : (filterGroup.type === 'group' ? 'dropdown' : filterGroup.type),
      label: filterGroup.title || filterGroup.label,
      options: actualOptions.map((opt: any) => ({
        id: opt.id,
        label: opt.label,
        value: opt.value ?? opt.id,
        checked: opt.checked ?? opt.selected ?? false,
      })),
      defaultValue: filterGroup.defaultValue || filterGroup.default,
    };
  });

  return {
    id: backendMetadata.id || 'operator_proficiency',
    version: backendMetadata.version || '1.0.0',
    filters,
  };
}

/**
 * Extract filter values from metadata for API calls
 */
export function extractFilterValues(metadata: OperatorProficiencyMetadata): Record<string, any> {
  const filterValues: Record<string, any> = {};

  metadata.filters.forEach((filter) => {
    if (filter.defaultValue !== undefined) {
      filterValues[filter.id] = filter.defaultValue;
    } else if (filter.type === 'multi-select' || filter.type === 'checkbox') {
      // For multi-select, get all checked options
      filterValues[filter.id] = filter.options
        .filter((opt) => opt.checked)
        .map((opt) => opt.value);
    } else {
      // For single-select (dropdown), get the checked option or first option
      const checkedOption = filter.options.find((opt) => opt.checked);
      filterValues[filter.id] = checkedOption?.value ?? filter.options[0]?.value ?? null;
    }
  });

  return filterValues;
}

/**
 * Get filter configuration by ID
 */
export function getFilterById(metadata: OperatorProficiencyMetadata, filterId: string): FilterConfig | undefined {
  return metadata.filters.find((f) => f.id === filterId);
}
